import React from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { ICD10Code } from '../../types';
import { Badge } from '../ui/badge';
import { t } from 'src/i18n';

interface ColoredHighlightedTextProps {
  text: string;
  codes: Array<ICD10Code & { system?: 'diagnosis' | 'pcs' }>;
  codeColors: Record<string,string>;
  onHighlightHover?: (codes: string[] | null) => void;
  hoveredCodes?: string[] | null;
  selectedCodes?: string[] | null;
  onHighlightClick?: (codes: string[]) => void;
  hideIrrelevant?: boolean;
  enableMarkdown?: boolean;
}

// Simple popover using title attribute; could be replaced with headless ui if needed
export const ColoredHighlightedText: React.FC<ColoredHighlightedTextProps> = React.memo(({ 
  text, 
  codes, 
  codeColors, 
  onHighlightHover, 
  hoveredCodes, 
  selectedCodes, 
  onHighlightClick, 
  hideIrrelevant,
  enableMarkdown = false 
}) => {
  const markdownWrapperClass = 'prose prose-sm dark:prose-invert max-w-none prose-headings:mt-6 prose-headings:mb-2 prose-p:my-3 prose-ul:my-3 prose-ol:my-3 prose-li:my-1';
  if (!codes || codes.length === 0) {
    // If markdown enabled but no codes, just render as markdown
    if (enableMarkdown) {
      return (
        <div className={markdownWrapperClass}>
          <ReactMarkdown 
            remarkPlugins={[remarkGfm, remarkBreaks]} 
          >
            {text}
          </ReactMarkdown>
        </div>
      );
    }
    return <>{text}</>;
  }

  // Build references
  const refs: Array<{ snippet: string; code: string; description: string; system: 'diagnosis' | 'pcs' }> = [];
  codes.forEach(code => {
    const system = code.system || 'diagnosis';
    if (code.text_references) {
      code.text_references.forEach((ref: any) => {
        const snippet = (ref.text_snippet || ref.text || '').trim();
        if (snippet) {
          refs.push({ snippet, code: code.code, description: code.explanation || code.description || '', system });
        }
      });
    }
  });
  if (refs.length === 0) {
    // If markdown enabled but no highlights, just render as markdown
    if (enableMarkdown) {
      return (
        <div className={markdownWrapperClass}>
          <ReactMarkdown 
            remarkPlugins={[remarkGfm, remarkBreaks]} 
          >
            {text}
          </ReactMarkdown>
        </div>
      );
    }
    return <>{text}</>;
  }

  // Helper function to render highlighted text segments (used both in plain and markdown modes)
  const renderHighlightedSegments = (segmentText: string) => {
    interface Highlight { start: number; end: number; codes: string[]; descriptions: string[]; systemsByCode: Record<string, 'diagnosis' | 'pcs'>; }
    const highlights: Highlight[] = [];

    refs.forEach(r => {
      const pattern = r.snippet.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      try {
        const regex = new RegExp(pattern, 'gi');
        let match: RegExpExecArray | null;
        while ((match = regex.exec(segmentText)) !== null) {
          const start = match.index;
          const end = start + match[0].length;
          const existing = highlights.find(h => (start <= h.end && end >= h.start));
          if (existing) {
            existing.start = Math.min(existing.start, start);
            existing.end = Math.max(existing.end, end);
            if (!existing.codes.includes(r.code)) {
              existing.codes.push(r.code);
              existing.descriptions.push(r.description);
              existing.systemsByCode[r.code] = r.system;
            }
          } else {
            highlights.push({ start, end, codes: [r.code], descriptions: [r.description], systemsByCode: { [r.code]: r.system } });
          }
        }
      } catch (_) { /* ignore bad regex */ }
    });

    if (highlights.length === 0) return segmentText;

    highlights.sort((a,b) => a.start - b.start);

    const segments: Array<{ text: string; highlight?: Highlight }> = [];
    let cursor = 0;
    highlights.forEach(h => {
      if (cursor < h.start) {
        segments.push({ text: segmentText.slice(cursor, h.start) });
      }
      segments.push({ text: segmentText.slice(h.start, h.end), highlight: h });
      cursor = h.end;
    });
    if (cursor < segmentText.length) segments.push({ text: segmentText.slice(cursor) });

    return (
      <>
        {segments.map((seg, idx) => {
          const h = seg.highlight;
          if (!h) return <span key={idx}>{seg.text}</span>;

          const pcsCodes = h.codes.filter((c) => (h.systemsByCode?.[c] || 'diagnosis') === 'pcs');
          const diagnosisCodes = h.codes.filter((c) => (h.systemsByCode?.[c] || 'diagnosis') !== 'pcs');
          const hasPcs = pcsCodes.length > 0;
          const hasDiagnosis = diagnosisCodes.length > 0;
          const isPcsOnly = hasPcs && !hasDiagnosis;

          // For PCS-only highlights, use a distinct underline style rather than the diagnosis background palette.
          const rawProvided = (hasDiagnosis ? diagnosisCodes : h.codes)
            .map(c => codeColors && codeColors[c])
            .filter(Boolean) as string[];

          const isCssColor = (v: string) => {
            if (!v) return false;
            const s = v.trim();
            if (s.startsWith('#')) return /^#[0-9A-Fa-f]{3,8}$/.test(s);
            if (s.startsWith('rgb') || s.startsWith('hsl')) return true;
            const keywords = ['transparent','currentColor','black','white','red','blue','green','yellow','gray'];
            if (keywords.includes(s.toLowerCase())) return true;
            return false;
          };

          const isTailwindClass = (v: string) => /(^|\s)(bg-|dark:|bg-)/.test(v);
          const tailwindClasses = rawProvided.filter(isTailwindClass);
          const providedColors = rawProvided.filter(isCssColor);

          const hashToHue = (s: string) => {
            let hash = 0;
            for (let i = 0; i < s.length; i++) {
              hash = (hash << 5) - hash + s.charCodeAt(i);
              hash |= 0;
            }
            return Math.abs(hash) % 360;
          };

          const hslForCode = (code: string) => {
            const hue = hashToHue(code);
            const saturation = 65;
            const lightness = 72;
            return { color: `hsl(${hue}, ${saturation}%, ${lightness}%)`, lightness };
          };
          const gen = (hasDiagnosis ? diagnosisCodes : h.codes).map(c => hslForCode(c));
          const genColors = gen.map(g => g.color);

          // PCS accent colors (rendered as a thin bottom bar so PCS references are visually distinct).
          const rawProvidedPcs = pcsCodes
            .map(c => codeColors && codeColors[c])
            .filter(Boolean) as string[];
          const providedColorsPcs = rawProvidedPcs.filter(isCssColor);
          const hslForPcsCode = (code: string) => {
            const hue = hashToHue(code);
            const saturation = 65;
            const lightness = 72;
            return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
          };
          const genColorsPcs = pcsCodes.map((c) => hslForPcsCode(c));
          const pcsColors = providedColorsPcs.length > 0 ? providedColorsPcs : genColorsPcs;
          const pcsUnderlineBg = pcsColors.length > 0 ? `linear-gradient(90deg, ${pcsColors.join(', ')})` : undefined;

          const useTailwindForSingle = tailwindClasses.length > 0 && tailwindClasses.length === 1;

          // Base highlight colors: diagnosis uses diagnosis palette; PCS-only uses PCS palette
          // so the highlighted snippet matches the PCS code swatch color.
          const colors = isPcsOnly
            ? pcsColors
            : (providedColors.length > 0 ? providedColors : genColors);

          const singleBg = colors.length === 1 ? colors[0] : undefined;
          const gradientBg = colors.length > 1 ? `linear-gradient(90deg, ${colors.join(', ')})` : undefined;

          let textColor = '#000';
          if (providedColors.length === 0 && gen.length > 0) {
            const avgLight = gen.reduce((s, g) => s + (g.lightness || 72), 0) / gen.length;
            textColor = avgLight > 60 ? '#000' : '#fff';
          }

          const tooltip = h.codes.map((c,i) => `${c}${h.descriptions[i] ? ': '+h.descriptions[i] : ''}`).join('\n');

          const tailClass = useTailwindForSingle ? tailwindClasses[0] : undefined;

          const isActive = !!(
            (hoveredCodes && hoveredCodes.some(hc => h.codes.includes(hc))) ||
            (selectedCodes && selectedCodes.some(sc => h.codes.includes(sc)))
          );

          // Keep a little extra bottom padding so the PCS underline bar doesn't feel cramped.
          const pcsDecorationClass = hasPcs ? 'pb-[1px]' : '';

          // PCS-only highlights: keep a subtle ring for visibility.
          // Background color now comes from the PCS codeColors palette.
          const pcsOnlyClass = isPcsOnly
            ? 'ring-1 ring-cyan-500/20 dark:ring-cyan-400/20'
            : '';

          return (
            <motion.span
              key={idx}
              title={tooltip}
              onMouseEnter={(e) => {
                if (e.buttons === 1 || window.getSelection()?.type === 'Range') return;
                onHighlightHover && onHighlightHover(h.codes);
              }}
              onMouseLeave={(e) => {
                if (e.buttons === 1 || window.getSelection()?.type === 'Range') return;
                onHighlightHover && onHighlightHover(null);
              }}
              onClick={() => {
                if (window.getSelection()?.type === 'Range') return;
                onHighlightClick && onHighlightClick(h.codes);
              }}
              transition={{ duration: 0.2 }}
              className={`px-0.5 rounded-sm cursor-pointer inline-block transition-all duration-200 ${tailClass || ''} ${pcsDecorationClass} ${pcsOnlyClass} ${isActive ? 'ring-2 ring-primary ring-offset-1 rounded-sm' : ''}`}
              style={(() => {
                const style: React.CSSProperties | undefined = tailClass
                  ? ({ color: textColor } as React.CSSProperties)
                  : ({ backgroundColor: singleBg, backgroundImage: gradientBg, color: textColor } as React.CSSProperties);

                // Add the PCS accent as a thin bottom bar (works for PCS-only and mixed highlights).
                if (hasPcs && pcsUnderlineBg) {
                  const existingBg = (style as any)?.backgroundImage;
                  (style as any).backgroundImage = existingBg ? `${existingBg}, ${pcsUnderlineBg}` : pcsUnderlineBg;
                  (style as any).backgroundRepeat = existingBg ? 'no-repeat, no-repeat' : 'no-repeat';
                  (style as any).backgroundPosition = existingBg ? '0 0, 0 100%' : '0 100%';
                  (style as any).backgroundSize = existingBg ? 'auto, 100% 3px' : '100% 3px';
                }

                return style;
              })()}
            >
              {seg.text}
            </motion.span>
          );
        })}
      </>
    );
  };

  // Helper to process React children and apply highlights to text nodes
  const processChildren = (children: React.ReactNode): React.ReactNode => {
    return React.Children.map(children, (child) => {
      if (typeof child === 'string') {
        return renderHighlightedSegments(child);
      }
      if (React.isValidElement(child) && child.props.children) {
        return React.cloneElement(child, {
          ...child.props,
          children: processChildren(child.props.children),
        } as any);
      }
      return child;
    });
  };

  const markdownComponents = React.useMemo(() => {
    return {
      p: ({ children, ...props }: any) => <p {...props}>{processChildren(children)}</p>,
      span: ({ children, ...props }: any) => <span {...props}>{processChildren(children)}</span>,
      strong: ({ children, ...props }: any) => <strong {...props}>{processChildren(children)}</strong>,
      em: ({ children, ...props }: any) => <em {...props}>{processChildren(children)}</em>,
      li: ({ children, ...props }: any) => <li {...props}>{processChildren(children)}</li>,
      h1: ({ children, ...props }: any) => <h1 {...props}>{processChildren(children)}</h1>,
      h2: ({ children, ...props }: any) => <h2 {...props}>{processChildren(children)}</h2>,
      h3: ({ children, ...props }: any) => <h3 {...props}>{processChildren(children)}</h3>,
      h4: ({ children, ...props }: any) => <h4 {...props}>{processChildren(children)}</h4>,
      h5: ({ children, ...props }: any) => <h5 {...props}>{processChildren(children)}</h5>,
      h6: ({ children, ...props }: any) => <h6 {...props}>{processChildren(children)}</h6>,
      blockquote: ({ children, ...props }: any) => <blockquote {...props}>{processChildren(children)}</blockquote>,
      td: ({ children, ...props }: any) => <td {...props}>{processChildren(children)}</td>,
      th: ({ children, ...props }: any) => <th {...props}>{processChildren(children)}</th>,
      a: ({ children, ...props }: any) => <a {...props}>{processChildren(children)}</a>,
      code: ({ inline, children, ...props }: any) => {
        if (inline) {
          return <code {...props}>{processChildren(children)}</code>;
        }
        return <code {...props}>{children}</code>;
      },
    };
  }, [codes, codeColors, hoveredCodes, selectedCodes]);

  // If markdown mode is enabled, use ReactMarkdown and inject highlights into text nodes
  if (enableMarkdown) {
    return (
      <div className={markdownWrapperClass}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkBreaks]}
          components={markdownComponents}
        >
          {text}
        </ReactMarkdown>
      </div>
    );
  }

  // Original plain text rendering logic below
  if (refs.length === 0) return <>{text}</>;

  // If requested, show only text segments that contain highlights with some context.
  // This ensures highlighted text is never cut while still hiding irrelevant content.
  if (hideIrrelevant) {
    // First, find all highlight positions
    const highlightRanges: Array<{ start: number; end: number }> = [];
    refs.forEach(r => {
      const pattern = r.snippet.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      try {
        const regex = new RegExp(pattern, 'gi');
        let match: RegExpExecArray | null;
        while ((match = regex.exec(text)) !== null) {
          const start = match.index;
          const end = start + match[0].length;
          highlightRanges.push({ start, end });
        }
      } catch (_) { /* ignore bad regex */ }
    });

    if (highlightRanges.length === 0) {
      return <>{text}</>;
    }

    // Sort and merge overlapping ranges
    highlightRanges.sort((a, b) => a.start - b.start);
    const mergedRanges: Array<{ start: number; end: number }> = [];
    let current = highlightRanges[0];
    for (let i = 1; i < highlightRanges.length; i++) {
      if (highlightRanges[i].start <= current.end) {
        current.end = Math.max(current.end, highlightRanges[i].end);
      } else {
        mergedRanges.push(current);
        current = highlightRanges[i];
      }
    }
    mergedRanges.push(current);

    // Expand each range with context (show surrounding sentences/paragraphs)
    const contextRanges: Array<{ start: number; end: number }> = [];
    const contextSize = 100; // characters of context around each highlight

    mergedRanges.forEach(range => {
      let start = Math.max(0, range.start - contextSize);
      let end = Math.min(text.length, range.end + contextSize);

      // Try to expand to sentence boundaries for better readability
      const sentenceRegex = /([^.!?]+[.!?]+(?:\s|$))/g;
      let match;
      while ((match = sentenceRegex.exec(text)) !== null) {
        const sentenceStart = match.index;
        const sentenceEnd = sentenceStart + match[0].length;

        if (sentenceStart <= range.start && sentenceEnd >= range.start) {
          // This sentence contains the highlight start
          start = sentenceStart;
        }
        if (sentenceStart <= range.end && sentenceEnd >= range.end) {
          // This sentence contains the highlight end
          end = sentenceEnd;
          break;
        }
      }

      contextRanges.push({ start, end });
    });

    // Merge overlapping context ranges
    contextRanges.sort((a, b) => a.start - b.start);
    const finalRanges: Array<{ start: number; end: number }> = [];
    let currentRange = contextRanges[0];
    for (let i = 1; i < contextRanges.length; i++) {
      if (contextRanges[i].start <= currentRange.end) {
        currentRange.end = Math.max(currentRange.end, contextRanges[i].end);
      } else {
        finalRanges.push(currentRange);
        currentRange = contextRanges[i];
      }
    }
    finalRanges.push(currentRange);

    // Build output with ellipsis between ranges
    const out: React.ReactNode[] = [];

    for (let index = 0; index < finalRanges.length; index++) {
      const range = finalRanges[index];
      const prevEnd = index > 0 ? finalRanges[index - 1].end : 0;
      if (range.start > prevEnd) {
        if (prevEnd > 0) {
          const omittedText = text.slice(prevEnd, range.start);
          // count words (simple split on whitespace) but clamp to at least 1
          const omittedWords = Math.max(1, omittedText.trim().split(/\s+/).filter(Boolean).length);
          out.push(
            <Badge key={`ellipsis-${index}`} variant="secondary" className="text-muted-foreground/80 text-center block my-2 px-2 py-1 bg-muted/20 border-border/50">
              … {t("home.omission", 'words omitted', { count: omittedWords })} …
            </Badge>
          );
        }
      }

      const segmentText = text.slice(range.start, range.end);
      if (enableMarkdown) {
        out.push(
          <div key={`segment-${index}`}>
            <ColoredHighlightedText
              text={segmentText}
              codes={codes}
              codeColors={codeColors}
              onHighlightHover={onHighlightHover}
              hoveredCodes={hoveredCodes}
              selectedCodes={selectedCodes}
              onHighlightClick={onHighlightClick}
              hideIrrelevant={false}
              enableMarkdown={enableMarkdown}
            />
          </div>
        );
      } else {
        out.push(
          <span key={`segment-${index}`}>
            <ColoredHighlightedText
              text={segmentText}
              codes={codes}
              codeColors={codeColors}
              onHighlightHover={onHighlightHover}
              hoveredCodes={hoveredCodes}
              selectedCodes={selectedCodes}
              onHighlightClick={onHighlightClick}
              hideIrrelevant={false}
              enableMarkdown={enableMarkdown}
            />
          </span>
        );
      }
    }

    return enableMarkdown ? <div>{out}</div> : <span>{out}</span>;
  }

  interface Highlight { start: number; end: number; codes: string[]; descriptions: string[]; systemsByCode: Record<string, 'diagnosis' | 'pcs'>; }
  const highlights: Highlight[] = [];

  refs.forEach(r => {
    const pattern = r.snippet.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    try {
      const regex = new RegExp(pattern, 'gi');
      let match: RegExpExecArray | null;
      while ((match = regex.exec(text)) !== null) {
        const start = match.index;
        const end = start + match[0].length;
        const existing = highlights.find(h => (start <= h.end && end >= h.start));
        if (existing) {
          existing.start = Math.min(existing.start, start);
          existing.end = Math.max(existing.end, end);
          if (!existing.codes.includes(r.code)) {
            existing.codes.push(r.code);
            existing.descriptions.push(r.description);
            existing.systemsByCode[r.code] = r.system;
          }
        } else {
          highlights.push({ start, end, codes: [r.code], descriptions: [r.description], systemsByCode: { [r.code]: r.system } });
        }
      }
    } catch (_) { /* ignore bad regex */ }
  });

  highlights.sort((a,b) => a.start - b.start);

  const segments: Array<{ text: string; highlight?: Highlight }> = [];
  let cursor = 0;
  highlights.forEach(h => {
    if (cursor < h.start) {
      segments.push({ text: text.slice(cursor, h.start) });
    }
    segments.push({ text: text.slice(h.start, h.end), highlight: h });
    cursor = h.end;
  });
  if (cursor < text.length) segments.push({ text: text.slice(cursor) });

  return (
    <span>
      {segments.map((seg, idx) => {
        const h = seg.highlight;
        if (!h) return <span key={idx}>{seg.text}</span>;

        const pcsCodes = h.codes.filter((c) => (h.systemsByCode?.[c] || 'diagnosis') === 'pcs');
        const diagnosisCodes = h.codes.filter((c) => (h.systemsByCode?.[c] || 'diagnosis') !== 'pcs');
        const hasPcs = pcsCodes.length > 0;
        const hasDiagnosis = diagnosisCodes.length > 0;
        const isPcsOnly = hasPcs && !hasDiagnosis;

        // Build colors for the codes. Prefer provided `codeColors` only if they're real CSS colors,
        // otherwise generate stable HSL per code. Many callers pass Tailwind class names (like 'bg-primary')
        // in `codeColors`; those are not valid inline CSS colors so we ignore them.
        // For PCS-only highlights, use a distinct underline style rather than the diagnosis background palette.
        const rawProvided = (hasDiagnosis ? diagnosisCodes : h.codes)
          .map(c => codeColors && codeColors[c])
          .filter(Boolean) as string[];

        const isCssColor = (v: string) => {
          if (!v) return false;
          const s = v.trim();
          if (s.startsWith('#')) return /^#[0-9A-Fa-f]{3,8}$/.test(s);
          if (s.startsWith('rgb') || s.startsWith('hsl')) return true;
          // basic color keyword check (common ones)
          const keywords = ['transparent','currentColor','black','white','red','blue','green','yellow','gray'];
          if (keywords.includes(s.toLowerCase())) return true;
          return false;
        };

        // detect Tailwind bg class strings (e.g. 'bg-rose-300 dark:bg-rose-600')
        const isTailwindClass = (v: string) => /(^|\s)(bg-|dark:|bg-)/.test(v);
        const tailwindClasses = rawProvided.filter(isTailwindClass);
        const providedColors = rawProvided.filter(isCssColor);

        // Stable hash -> hue in [0,360)
        const hashToHue = (s: string) => {
          let hash = 0;
          for (let i = 0; i < s.length; i++) {
            hash = (hash << 5) - hash + s.charCodeAt(i);
            hash |= 0;
          }
          return Math.abs(hash) % 360;
        };

        const hslForCode = (code: string) => {
          const hue = hashToHue(code);
          const saturation = 65;
          const lightness = 72; // less pale so color is noticeable
          // use comma-separated HSL for broader compatibility
          return { color: `hsl(${hue}, ${saturation}%, ${lightness}%)`, lightness };
        };
        const gen = (hasDiagnosis ? diagnosisCodes : h.codes).map(c => hslForCode(c));
        const genColors = gen.map(g => g.color);

        // PCS accent colors (rendered as a thin bottom bar so PCS references are visually distinct).
        const rawProvidedPcs = pcsCodes
          .map(c => codeColors && codeColors[c])
          .filter(Boolean) as string[];
        const providedColorsPcs = rawProvidedPcs.filter(isCssColor);
        const hslForPcsCode = (code: string) => {
          const hue = hashToHue(code);
          const saturation = 65;
          const lightness = 72;
          return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
        };
        const genColorsPcs = pcsCodes.map((c) => hslForPcsCode(c));
        const pcsColors = providedColorsPcs.length > 0 ? providedColorsPcs : genColorsPcs;
        const pcsUnderlineBg = pcsColors.length > 0 ? `linear-gradient(90deg, ${pcsColors.join(', ')})` : undefined;

        // If we have Tailwind class colors for any code and it's a single-code highlight
        // prefer applying the Tailwind class to the span (so existing class tokens continue to work).
        const useTailwindForSingle = tailwindClasses.length > 0 && tailwindClasses.length === 1;

        // Base highlight colors: diagnosis uses diagnosis palette; PCS-only uses PCS palette
        // so the highlighted snippet matches the PCS code swatch color.
        const colors = isPcsOnly
          ? pcsColors
          : (providedColors.length > 0 ? providedColors : genColors);

        // Build background: solid for single, gradient for multiple codes
        const singleBg = colors.length === 1 ? colors[0] : undefined;
        const gradientBg = colors.length > 1 ? `linear-gradient(90deg, ${colors.join(', ')})` : undefined;

        // Determine text color: if generated, we know lightness; otherwise default to black
        let textColor = '#000';
        if (providedColors.length === 0 && gen.length > 0) {
          const avgLight = gen.reduce((s, g) => s + (g.lightness || 72), 0) / gen.length;
          textColor = avgLight > 60 ? '#000' : '#fff';
        }

        const tooltip = h.codes.map((c,i) => `${c}${h.descriptions[i] ? ': '+h.descriptions[i] : ''}`).join('\n');

        // If using Tailwind class for single color, add it to the className and don't set inline background
        const tailClass = useTailwindForSingle ? tailwindClasses[0] : undefined;

        const isActive = !!(
          (hoveredCodes && hoveredCodes.some(hc => h.codes.includes(hc))) ||
          (selectedCodes && selectedCodes.some(sc => h.codes.includes(sc)))
        );

        // Keep a little extra bottom padding so the PCS underline bar doesn't feel cramped.
        const pcsDecorationClass = hasPcs ? 'pb-[1px]' : '';

        const pcsOnlyClass = isPcsOnly
          ? 'ring-1 ring-cyan-500/20 dark:ring-cyan-400/20'
          : '';
        return (
          <motion.span
            key={idx}
            title={tooltip}
            onMouseEnter={(e) => {
              if (e.buttons === 1 || window.getSelection()?.type === 'Range') return;
              onHighlightHover && onHighlightHover(h.codes);
            }}
            onMouseLeave={(e) => {
              if (e.buttons === 1 || window.getSelection()?.type === 'Range') return;
              onHighlightHover && onHighlightHover(null);
            }}
            onClick={() => {
              if (window.getSelection()?.type === 'Range') return;
              onHighlightClick && onHighlightClick(h.codes);
            }}
            transition={{ duration: 0.2 }}
            className={`px-0.5 rounded-sm cursor-pointer inline-block transition-all duration-200 ${tailClass || ''} ${pcsDecorationClass} ${pcsOnlyClass} ${isActive ? 'ring-2 ring-primary ring-offset-1 rounded-sm' : ''}`}
            style={(() => {
              const style: React.CSSProperties | undefined = tailClass
                ? ({ color: textColor } as React.CSSProperties)
                : ({ backgroundColor: singleBg, backgroundImage: gradientBg, color: textColor } as React.CSSProperties);

              // Add the PCS accent as a thin bottom bar (works for PCS-only and mixed highlights).
              if (hasPcs && pcsUnderlineBg) {
                const existingBg = (style as any)?.backgroundImage;
                (style as any).backgroundImage = existingBg ? `${existingBg}, ${pcsUnderlineBg}` : pcsUnderlineBg;
                (style as any).backgroundRepeat = existingBg ? 'no-repeat, no-repeat' : 'no-repeat';
                (style as any).backgroundPosition = existingBg ? '0 0, 0 100%' : '0 100%';
                (style as any).backgroundSize = existingBg ? 'auto, 100% 3px' : '100% 3px';
              }

              return style;
            })()}
          >
            {seg.text}
          </motion.span>
        );
      })}
    </span>
  );
});
